import type {
  AttemptResult,
  FeedbackItem,
  FeedbackKind,
  PracticeSession,
  Script,
  Show,
  ShowNotes,
  ShowProgress,
  ShowSummary,
  UserSettings,
  UserStats,
} from '@/types/domain';
import { DEFAULT_SETTINGS } from '@/types/domain';
import { ApiError, type ApiClient } from '@/services/api/apiClient';
import { applyAttempt, applySession } from '@/services/progress/progressService';
import { newId } from '@/lib/id';
import * as local from '@/services/storage/db';

/**
 * Per-line last-write-wins merge.
 *
 * Rehearsing offline on a phone while a laptop holds older state is the normal
 * case, so remote and local progress are reconciled line by line on timestamp
 * rather than one wholesale overwriting the other.
 */
export function mergeProgress(a: ShowProgress, b: ShowProgress): ShowProgress {
  const lineStats = { ...a.lineStats };
  for (const [lineId, incoming] of Object.entries(b.lineStats)) {
    const existing = lineStats[lineId];
    if (!existing || incoming.updatedAt > existing.updatedAt) lineStats[lineId] = incoming;
  }
  return {
    showId: a.showId,
    lineStats,
    updatedAt: a.updatedAt > b.updatedAt ? a.updatedAt : b.updatedAt,
  };
}

export function emptyProgress(showId: string): ShowProgress {
  return { showId, lineStats: {}, updatedAt: new Date().toISOString() };
}

export function emptyStats(): UserStats {
  return {
    totalPracticeMs: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastPracticeDate: null,
    dailyMs: {},
    updatedAt: new Date().toISOString(),
  };
}

const isOfflineError = (cause: unknown): boolean =>
  cause instanceof ApiError && (cause.isOffline || cause.status >= 500);

/**
 * Offline-first facade over the API and IndexedDB.
 *
 * Reads resolve from IndexedDB immediately and refresh from the network in the
 * background; writes land locally first and are queued for replay, so a
 * rehearsal never blocks on connectivity.
 */
export class DataService {
  constructor(private readonly api: ApiClient) {}

  // --- shows ---------------------------------------------------------------

  async listShows(): Promise<ShowSummary[]> {
    const cached = (await local.localListShows()).map(withRoles);
    try {
      const { data } = await this.api.get<ShowSummary[]>('shows');
      const shows = data.map(withRoles);
      await Promise.all(shows.map((show) => local.localPutShow(show)));
      const remoteIds = new Set(shows.map((s) => s.showId));
      // Only prune locally-cached shows once we know the server list is authoritative.
      await Promise.all(
        cached.filter((s) => !remoteIds.has(s.showId)).map((s) => local.localDeleteShow(s.showId)),
      );
      return sortShows(shows);
    } catch (cause) {
      if (isOfflineError(cause)) return sortShows(cached);
      throw cause;
    }
  }

  async getShow(showId: string): Promise<ShowSummary | null> {
    const cached = await local.localGetShow(showId);
    try {
      const { data } = await this.api.get<Show>(`shows/${showId}`);
      const show = withRoles(data);
      await local.localPutShow(show);
      return show;
    } catch (cause) {
      if (isOfflineError(cause)) return cached ? withRoles(cached) : null;
      if (cause instanceof ApiError && cause.status === 404) return null;
      throw cause;
    }
  }

  async createShow(title: string, script: Script, myRoles: string[]): Promise<ShowSummary> {
    const showId = script.showId || newId('show');
    const now = new Date().toISOString();
    const summary: ShowSummary = {
      showId,
      title,
      myRoles,
      lineCount: countLines(script),
      myLineCount: countLines(script, myRoles),
      sceneCount: script.scenes.length,
      createdAt: now,
      updatedAt: now,
    };

    await local.localPutShow(summary);
    await local.localPutScript({ ...script, showId, title, updatedAt: now });
    await local.localPutProgress(emptyProgress(showId));

    await this.writeThrough('PUT', `shows/${showId}`, summary, `show:${showId}`);
    await this.writeThrough('PUT', `shows/${showId}/script`, { ...script, showId, title }, `script:${showId}`);
    return summary;
  }

  async updateShow(show: ShowSummary): Promise<ShowSummary> {
    const updated = { ...show, updatedAt: new Date().toISOString() };
    await local.localPutShow(updated);
    await this.writeThrough('PUT', `shows/${show.showId}`, updated, `show:${show.showId}`);
    return updated;
  }

  async setMyRoles(showId: string, myRoles: string[]): Promise<ShowSummary | null> {
    const [show, script] = await Promise.all([local.localGetShow(showId), local.localGetScript(showId)]);
    if (!show) return null;
    return this.updateShow({
      ...show,
      myRoles,
      myLineCount: script ? countLines(script, myRoles) : show.myLineCount,
    });
  }

  async deleteShow(showId: string): Promise<void> {
    await local.localDeleteShow(showId);
    await this.writeThrough('DELETE', `shows/${showId}`, null, `show:${showId}`);
  }

  // --- script --------------------------------------------------------------

  async getScript(showId: string): Promise<Script | null> {
    const cached = await local.localGetScript(showId);
    try {
      const { data } = await this.api.get<Script>(`shows/${showId}/script`);
      await local.localPutScript(data);
      return data;
    } catch (cause) {
      if (isOfflineError(cause)) return cached ?? null;
      if (cause instanceof ApiError && cause.status === 404) return cached ?? null;
      throw cause;
    }
  }

  async saveScript(script: Script): Promise<void> {
    const updated = { ...script, updatedAt: new Date().toISOString() };
    await local.localPutScript(updated);
    await this.writeThrough('PUT', `shows/${script.showId}/script`, updated, `script:${script.showId}`);
  }

  /**
   * Swaps in a corrected script while keeping progress.
   *
   * Line ids are derived from line content, so any line whose text survives the
   * re-import keeps its counters. Returns how many matched so the UI can be
   * honest about what carried over.
   */
  async replaceScript(
    showId: string,
    script: Omit<Script, 'showId' | 'updatedAt'>,
  ): Promise<{ carriedOver: number; total: number; orphaned: number }> {
    const [show, progress] = await Promise.all([
      local.localGetShow(showId),
      local.localGetProgress(showId),
    ]);

    const next: Script = { ...script, showId, updatedAt: new Date().toISOString() };
    await this.saveScript(next);

    const myRoles = show?.myRoles ?? [];
    if (show) {
      await this.updateShow({
        ...show,
        title: next.title,
        lineCount: countLines(next),
        myLineCount: countLines(next, myRoles),
        sceneCount: next.scenes.length,
      });
    }

    const newIds = new Set(
      next.scenes.flatMap((scene) =>
        scene.lines.filter((line) => line.kind === 'dialogue').map((line) => line.lineId),
      ),
    );
    const tracked = Object.keys(progress?.lineStats ?? {});
    const carriedOver = tracked.filter((lineId) => newIds.has(lineId)).length;

    return { carriedOver, total: tracked.length, orphaned: tracked.length - carriedOver };
  }

  // --- progress ------------------------------------------------------------

  async getProgress(showId: string): Promise<ShowProgress> {
    const cached = (await local.localGetProgress(showId)) ?? emptyProgress(showId);
    try {
      const { data } = await this.api.get<ShowProgress>(`shows/${showId}/progress`);
      const merged = mergeProgress(cached, data);
      await local.localPutProgress(merged);
      return merged;
    } catch (cause) {
      if (isOfflineError(cause) || (cause instanceof ApiError && cause.status === 404)) return cached;
      throw cause;
    }
  }

  async recordAttempt(showId: string, lineId: string, result: AttemptResult): Promise<ShowProgress> {
    const current = (await local.localGetProgress(showId)) ?? emptyProgress(showId);
    const next = applyAttempt(current, lineId, result);
    await local.localPutProgress(next);
    // PATCH carries only the touched line so concurrent devices do not clobber each other.
    await this.writeThrough(
      'PATCH',
      `shows/${showId}/progress`,
      { lineStats: { [lineId]: next.lineStats[lineId] } },
      `progress:${showId}:${lineId}`,
    );
    return next;
  }

  // --- notes ---------------------------------------------------------------

  async getNotes(showId: string): Promise<ShowNotes> {
    const cached = (await local.localGetNotes(showId)) ?? {};
    try {
      const { data } = await this.api.get<ShowNotes>(`shows/${showId}/notes`);
      await local.localPutNotes(showId, data);
      return data;
    } catch (cause) {
      if (isOfflineError(cause) || (cause instanceof ApiError && cause.status === 404)) return cached;
      throw cause;
    }
  }

  async saveNote(
    showId: string,
    lineId: string,
    patch: Partial<{ note: string; translation: string; coaching: string }>,
  ): Promise<ShowNotes> {
    const notes = (await local.localGetNotes(showId)) ?? {};
    const existing = notes[lineId] ?? { note: '', translation: '', coaching: '', updatedAt: '' };
    const next: ShowNotes = {
      ...notes,
      [lineId]: { ...existing, ...patch, updatedAt: new Date().toISOString() },
    };
    await local.localPutNotes(showId, next);
    await this.writeThrough('PUT', `shows/${showId}/notes`, next, `notes:${showId}`);
    return next;
  }

  // --- settings ------------------------------------------------------------

  async getSettings(): Promise<UserSettings> {
    const cached = (await local.localGetSettings()) ?? DEFAULT_SETTINGS;
    try {
      const { data } = await this.api.get<{ settings?: UserSettings }>('me');
      const settings = { ...DEFAULT_SETTINGS, ...(data?.settings ?? {}) };
      await local.localPutSettings(settings);
      return settings;
    } catch (cause) {
      if (isOfflineError(cause) || (cause instanceof ApiError && cause.status === 404)) return cached;
      throw cause;
    }
  }

  async saveSettings(settings: UserSettings): Promise<UserSettings> {
    await local.localPutSettings(settings);
    await this.writeThrough('PUT', 'me', { settings }, 'settings');
    return settings;
  }

  // --- sessions & stats ----------------------------------------------------

  async getStats(): Promise<UserStats> {
    const cached = (await local.localGetStats()) ?? emptyStats();
    try {
      const { data } = await this.api.get<UserStats>('stats');
      await local.localPutStats(data);
      return data;
    } catch (cause) {
      if (isOfflineError(cause) || (cause instanceof ApiError && cause.status === 404)) return cached;
      throw cause;
    }
  }

  async recordSession(session: PracticeSession): Promise<UserStats> {
    const stats = applySession((await local.localGetStats()) ?? emptyStats(), session);
    await local.localPutStats(stats);
    await this.writeThrough('POST', 'sessions', session, `session:${session.sessionId}`);
    return stats;
  }

  // --- sync ----------------------------------------------------------------

  private async writeThrough(
    method: local.OutboxEntry['method'],
    path: string,
    body: unknown,
    dedupeKey: string,
  ): Promise<void> {
    try {
      await this.send(method, path, body);
    } catch (cause) {
      if (!isOfflineError(cause)) throw cause;
      await local.enqueue({ method, path, body, dedupeKey });
    }
  }

  private send(method: local.OutboxEntry['method'], path: string, body: unknown): Promise<unknown> {
    switch (method) {
      case 'POST':
        return this.api.post(path, body);
      case 'PUT':
        return this.api.put(path, body);
      case 'PATCH':
        return this.api.patch(path, body);
      case 'DELETE':
        return this.api.delete(path);
    }
  }

  /** Replays queued writes. Safe to call repeatedly; returns how many drained. */
  async flushOutbox(): Promise<number> {
    const entries = await local.peekOutbox();
    let flushed = 0;

    for (const entry of entries) {
      if (entry.id === undefined) continue;
      try {
        await this.send(entry.method, entry.path, entry.body);
        await local.dropOutboxEntry(entry.id);
        flushed += 1;
      } catch (cause) {
        if (isOfflineError(cause)) break;
        // A rejected write will never succeed on replay, so drop it rather than wedge the queue.
        await local.dropOutboxEntry(entry.id);
      }
    }

    return flushed;
  }

  pendingWrites(): Promise<number> {
    return local.outboxSize();
  }

  // --- feedback ------------------------------------------------------------
  // Shared between users and small, so it is always read live rather than cached.

  async listFeedback(): Promise<FeedbackItem[]> {
    const { data } = await this.api.get<FeedbackItem[]>('feedback');
    return data;
  }

  async postFeedback(kind: FeedbackKind, title: string, detail: string): Promise<FeedbackItem[]> {
    const { data } = await this.api.post<FeedbackItem[]>('feedback', { kind, title, detail });
    return data;
  }

  async voteFeedback(id: string, wanted: boolean): Promise<FeedbackItem[]> {
    const path = `feedback/${id}/vote`;
    const { data } = wanted
      ? await this.api.post<FeedbackItem[]>(path, {})
      : await this.api.delete<FeedbackItem[]>(path);
    return data;
  }
}

/** A show stored before doubling, or a malformed one, has no role list. */
function withRoles<T extends { myRoles?: string[] }>(show: T): T & { myRoles: string[] } {
  return { ...show, myRoles: Array.isArray(show.myRoles) ? show.myRoles : [] };
}

function countLines(script: Script, characters?: string[]): number {
  const mine = characters ? new Set(characters) : null;
  return script.scenes.reduce(
    (total, scene) =>
      total +
      scene.lines.filter(
        (line) => line.kind === 'dialogue' && (mine === null || mine.has(line.character)),
      ).length,
    0,
  );
}

function sortShows(shows: ShowSummary[]): ShowSummary[] {
  return [...shows].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
